import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useEditSeasonMutation } from 'common/api/endpoints/harvestData';
import { handleError } from 'common/api/utils/handleError';
import { WizardDropdownOption } from 'common/components/WizardControls/WizardDropdown';
import { Season } from 'common/models/season';
import * as notifier from 'common/services/notification';
import {
  SeasonSelector,
  SelectedSeason,
} from 'features/navbar/components/SeasonSelector/SeasonSelector';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  CancelBtn,
  ChangeBtn,
  FormWarning,
  InputWrapper,
  SaveBtn,
  SeasonFormWrapper,
} from './styles';

export const DefaultSeasonForm: FC<{
  defaultSeason: WizardDropdownOption<Season> | null;
  seasons: Season[];
  isLoading: boolean;
}> = ({ defaultSeason, seasons, isLoading }) => {
  const [editDefaultSeason] = useEditSeasonMutation();
  const [isEditing, setIsEditing] = useState(false);

  const methods = useForm<SelectedSeason>({
    values: { season: defaultSeason },
  });
  const {
    control,
    formState: { isDirty },
    handleSubmit,
    reset,
  } = methods;

  const closeForm = () => {
    reset();
    setIsEditing(false);
  };

  const onSubmit = async (data: SelectedSeason): Promise<void> => {
    if (!isDirty) {
      closeForm();
      return;
    }

    if (data.season?.value.id) {
      const dto = { id: data.season.value.id, isDefault: true };

      try {
        await editDefaultSeason([dto]).unwrap();
        notifier.showSuccessMessage(
          'Default season updated. Refresh to see the change in other screens.',
        );
        closeForm();
      } catch (e) {
        handleError(
          e,
          `Unable to make season ${data.season?.label} the default season.`,
        );
      }
    }
  };

  return (
    <SeasonFormWrapper onSubmit={handleSubmit(onSubmit)} isEditing={isEditing}>
      <InputWrapper>
        <span>Default Season:</span>
        <SeasonSelector
          control={control}
          seasons={seasons || []}
          isLoading={isLoading}
          isDisabled={!isEditing}
        />
        {isEditing ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <SaveBtn type='submit'>Save</SaveBtn>
            <CancelBtn type='button' onClick={closeForm}>
              Cancel
            </CancelBtn>
          </div>
        ) : (
          <ChangeBtn type='button' onClick={() => setIsEditing(true)}>
            Change
          </ChangeBtn>
        )}
      </InputWrapper>
      {isEditing && (
        <FormWarning>
          <ErrorOutlineIcon />
          <span>Warning: changing this value will affect all users.</span>
        </FormWarning>
      )}
    </SeasonFormWrapper>
  );
};
